import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api.js';
import { ImageIcon, Upload, Trash2 } from 'lucide-react';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
const MAX_BYTES = 2 * 1024 * 1024;

const LogoUpload: React.FC = () => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    void loadLogo();
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const applyObjectUrl = (url: string | null) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = url;
    setLogoUrl(url);
  };

  const loadLogo = async () => {
    try {
      const res = await api.get<Blob>('/team-settings/logo', { responseType: 'blob' });
      applyObjectUrl(URL.createObjectURL(res.data));
    } catch {
      applyObjectUrl(null);
    }
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Select a file first');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only PNG, JPEG, or SVG files are allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File must be under 2 MB');
      return;
    }

    const formData = new FormData();
    formData.append('logo', file);

    setUploading(true);
    try {
      await api.post('/team-settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadLogo();
      toast.success('Logo uploaded');
      if (fileRef.current) fileRef.current.value = '';
    } catch {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete('/team-settings/logo');
      applyObjectUrl(null);
      toast.success('Logo removed');
    } catch {
      toast.error('Failed to remove logo');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center w-48 h-24 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl overflow-hidden">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Company logo"
            className="max-w-full max-h-full object-contain p-2"
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-300">
            <ImageIcon size={28} />
            <span className="text-xs font-black uppercase tracking-widest">No logo</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml"
          className="hidden"
          id="logo-file-input"
        />
        <label
          htmlFor="logo-file-input"
          className="px-4 py-2 bg-gray-50 border-2 border-gray-100 rounded-xl font-bold text-sm text-gray-700 cursor-pointer hover:border-brand-500 transition-colors"
        >
          Choose file
        </label>
        <button
          type="button"
          onClick={() => void handleUpload()}
          disabled={uploading}
          className="px-4 py-2 bg-brand-600 text-white rounded-xl font-black uppercase tracking-widest text-xs hover:bg-brand-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
        >
          <Upload size={14} />
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        {logoUrl && (
          <button
            type="button"
            onClick={() => void handleRemove()}
            className="px-4 py-2 bg-red-50 border-2 border-red-100 text-red-600 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-red-100 transition-all active:scale-95 flex items-center gap-2"
          >
            <Trash2 size={14} />
            Remove
          </button>
        )}
      </div>
      <p className="text-xs text-gray-400 font-medium">PNG, JPEG or SVG · Max 2 MB</p>
    </div>
  );
};

export default LogoUpload;
